import { formatDate } from "@/utils/date-format";

export interface UpcomingDate {
  title: string;
  dateLabel: string;
  daysAway: number;
}

const DAY_MS = 86_400_000;

/**
 * The `limit` nearest UPCOMING occurrences of a set of ANNUAL observances (Valentine's Day,
 * Thanksgiving, …). Each raw entry is a recurring month/day that the backend pins to some year, so
 * we roll it to its NEXT occurrence — this calendar year if still ahead, otherwise next year. The
 * widget therefore never empties as the year winds down; near year-end the list naturally spills
 * into January. Rolling forward also means nothing is ever in the past, so no separate filter is
 * needed.
 *
 * Day counts are whole CALENDAR days from local midnight today (today = 0), so a date "today" reads
 * as 0 regardless of the time of day, and DST shifts round to the nearest whole day.
 */
export function toUpcomingDates(
  raw: readonly { title: string; date: string }[],
  now: Date = new Date(),
  limit = 5,
): UpcomingDate[] {
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  return raw
    .map(({ title, date }) => {
      const month = date.slice(5, 7); // "MM" from "YYYY-MM-DD"
      const day = date.slice(8, 10); // "DD"
      const monthIdx = Number(month) - 1;
      const dayNum = Number(day);
      let year = now.getFullYear();
      let occMs = new Date(year, monthIdx, dayNum).getTime();
      if (occMs < todayMs) {
        year += 1;
        occMs = new Date(year, monthIdx, dayNum).getTime();
      }
      return {
        title,
        dateLabel: formatDate(`${year}-${month}-${day}`),
        daysAway: Math.round((occMs - todayMs) / DAY_MS),
      };
    })
    .sort((a, b) => a.daysAway - b.daysAway)
    .slice(0, limit);
}
